import { ConnectionSignStoreAdapter } from '../app/_infrastructure/adapters/store/connection-sign/connection-sign.adapter';
import { catchError, finalize, take, tap } from 'rxjs/operators';
import { Injectable, OnDestroy } from '@angular/core';
import {
  FileDetailsDTO,
  SignatureDTO,
  SignatureValidationCode,
  I18nData
} from '../app/api/plyca-dtos';
import { Configuration } from '../app/conf/app.constants';
import { Subject, Observable, throwError, EMPTY } from 'rxjs';
import { DocumentService } from '../app/documents/document.service';
import { TranslateService } from '@ngx-translate/core';
import { UIService } from '../app/ui.service';
import { WaitLoadingService } from '../app/components/common/waitLoading/wait-loading.service';
import { BaseDirectiveObject } from '../app/components/common/base-directive-object';
import { PlycaErrorHandler } from '../http/PlycaErrorHandler';
import { AfirmaSignatureProvider } from './signature/services/afirma.signature-provider';
import {
  CertificateSelectionResponse,
  SignatureProviderType,
  SignatureProviderService,
  SignatureRequest, SignatureResponse, SignatureStatus
} from './signature/signature.objects';
import { UserService } from '../app/user.service';
import { MockSignatureProvider } from './signature/services/mock.signature-provider';
import { SignatureUtils } from './signature.utils';
import { PasswordSignatureProvider } from './signature/services/password.signature-provider';



// Adaptacion de PlycaFirma
declare let MaxiApplet: any;
const MAX_MILIS = 60000;

/**
 * Funcionamiento:
 * Modo 1 (multiples ficheros):
 * 1. Suscribirse desde la clase que invocará al metodo de firma al observable signatures.
 * 2. Invocar a metodo sign. Este método invoca al miniapplet de @firma. En caso de firma correcta la sube al servidor, devuelve
 * los datos del fichero firmado con el conjunto de firmas incorporadas, y emite la firma a la suscripcion anterior
 *
 * Modo 2 (1 solo fichero):
 * 1. Invocar a metodo sign. Este método invoca al miniapplet de @firma. En caso de firma correcta la sube al servidor, devuelve
 * los datos del fichero firmado con el conjunto de firmas incorporadas y emite la firma en el observale devuelto por esta funcion
 */
@Injectable()
export class SignatureService extends BaseDirectiveObject implements OnDestroy {

  private maxiAppletCargado = false;
  private signatures: Subject<SignatureResponse>;

  // INFORMACION DE LA PANTALLA ALMACENADA EN SESION
  // userPrefs: UserPrefs;

  private pendingSignatures = new Array<SignatureRequest>();

  constructor(
    private _configuration: Configuration,
    public documentService: DocumentService,
    protected translateService: TranslateService,
    protected uiService: UIService,
    private plycaErrorHandler: PlycaErrorHandler,
    private waitService: WaitLoadingService,
    private userService: UserService,
    private afirmaSignatureProvider: AfirmaSignatureProvider,
    private passwordSignatureProvider: PasswordSignatureProvider,
    private mocksignatureProvider: MockSignatureProvider,
    private signatureUtils: SignatureUtils,
    private readonly _connectionSignStoreAdapter: ConnectionSignStoreAdapter,
  ) {
    super();
    this.signatures = new Subject() as Subject<SignatureResponse>;
  }

  ngOnDestroy(): void {
    this.signatures.complete();
    super.ngOnDestroy();
  }

  public getSignatures(): Observable<SignatureResponse> {
    return this.signatures;
  }


  public sign(request: SignatureRequest): Observable<any> {
    this.pendingSignatures.push(request);
    if (this.pendingSignatures.length === 1) {
      this.signNexSignature();
    }
    return request.subscription;
  }

  public cancelAllPendingSignatures() {
    this.pendingSignatures = [];
  }

  private signNexSignature() {
    // get next request, if any.
    if (this.pendingSignatures.length > 0) {
      const signatureRequest = this.pendingSignatures[0];
      this.executeSign(signatureRequest);
    }
  }

  public isSignatureProviderEnabled(type: SignatureProviderType): boolean {
    return this.getSignatureProvider(type).isEnabled();
  }

  private executeSign(request: SignatureRequest): void {
    this.subscribe(this.getSignatureProvider(request.provider).sign(request).pipe(
      take(1),
      tap((signature: SignatureResponse) => {
        this.signatures.next(signature);
        request.subscription.next(signature);
      }),
      catchError((error: any) => {
        this.manageSignatureError(error);
        return throwError(error);
      }),
      finalize(() => this.consumeSignatureRequest())
    ));
  }

  private getSignatureProvider(provider: SignatureProviderType): SignatureProviderService {
    const auxProvider = this.signatureUtils.getSignatureProvider(provider);
    let providerService: SignatureProviderService;
    if (auxProvider?.type === SignatureProviderType.PASSWORD_PROVIDER) {
      providerService = this.passwordSignatureProvider;
    } else if (auxProvider?.type === SignatureProviderType.MOCK) {
      providerService = this.mocksignatureProvider;
    } else {
      providerService = this.afirmaSignatureProvider;
    }
    return providerService;
  }


  private manageSignatureError(msgKey = 'Upload.error.signature', error?: any): void {
    if (!this.pendingSignatures.length) {
      this.waitService.cancelWaiting();

      // De momento para errores de sellado concretamos el mensaje
      // No hemos querido abrir a pintar todos los errores de servidor porque no sabemos si están correctamente establecidos
      if (error && error.error.errors && error.error.errors.length > 0) {
        const hasUpgradeError = error.error.errors.some((errorItem) => {
          return errorItem.validationCode && errorItem.validationCode.includes('UPGRADE');
        });

        if (hasUpgradeError) {
          this.plycaErrorHandler.showServerError(error);
        } else {
          this.uiService.showMessage(new I18nData(msgKey));
        }
      } else {
        this.uiService.showMessage(new I18nData(msgKey));
      }
    }
  }

  private consumeSignatureRequest(): void {
    this.pendingSignatures.shift();
    this.signNexSignature();
  }

  /**
   * Método de seleccion de certificado. Puede ser utilizado para firmas consecutivas
   * con el mismo certificado. Para ello en la operación de firma habrá que pasar
   * el parametro 'encodedcert:'+certificateB64;
   *
   * @returns Observable con el certificado seleccionado en base64, undefined si
   * no se seleccionó nada
   */
  public selectCertificate(provider?: SignatureProviderType): Observable<CertificateSelectionResponse> {
    return this.getSignatureProvider(provider).selectCertificate().pipe(take(1));
  }

  public startFerramenta(successCallback?: any, errorCallback?: any): void {
    MaxiApplet.startFerramenta(successCallback, errorCallback);
  }

  public stopFerramenta(successCallback?: any): void {
    const errorCallback = function (type: string, message: string): void {
      successCallback();
    }.bind(this);
    MaxiApplet.stopFerramenta(successCallback, errorCallback);
  }

  /*
   * Inicia AutoFirma y chequea la conexión
   */
  public check(successCallback?: any, errorCallback?: any, provider?: SignatureProviderType): void {
    this.subscribe(this.getSignatureProvider(provider).check().pipe(
      tap((response: SignatureStatus) => {
        if (response === SignatureStatus.OK) {
          successCallback();
          this._connectionSignStoreAdapter.setConnectionStatus(true);
        } else if (response === SignatureStatus.ERROR) {
          errorCallback();
          this._connectionSignStoreAdapter.setConnectionStatus(false);
        }
      }),
      catchError((args: any) => {
        console.error('Error al conectar con autofirma ', args);
        errorCallback(args);
        return EMPTY;
      })
    ));
  }

  // Descifrado de fragmentos
  public selectCertAndDecipher(
    digestAlgorithm: string,
    thumbprint: string,
    b64Fragments: Array<string>,
    decipherSuccessCallback?: any,
    decipherErrorCallback?: any
  ): void {
    console.log('Descifrando fragmentos');

    // afirma callbacks
    if (!decipherSuccessCallback) {
      decipherSuccessCallback = function (b64Secrets: Array<string>): void {
        console.log('Descifrado correcto: [' + b64Secrets + ']');
      }.bind(this);
    } else {
      //      decipherSuccessCallback.bind(this);
    }

    if (!decipherErrorCallback) {
      decipherErrorCallback = function (type: string, message: string): void {
        console.error('Error al descifrar', type, message);
      }.bind(this);
    } else {
      //      decipherErrorCallback.bind(this);
    }

    this.internalSelectCertAndDecipher(
      digestAlgorithm,
      thumbprint,
      b64Fragments,
      decipherSuccessCallback,
      decipherErrorCallback
    );
  }

  /**
   *
   * @param stick
   * @deprecated usar SignatureProviderRequest.certificate y pasar parametro con certificado
   */
  public stickCertificate(stick: boolean): void {
    this.afirmaSignatureProvider.setStickySignatory(stick);
    MaxiApplet.setStickySignatory(stick);
  }

  public isValidSignatureCode(code: SignatureValidationCode): boolean {
    return code === undefined || code === SignatureValidationCode.OK;
  }

  /**
   * Opens a modal window inside an existing component.
   * @param content The content to display, either string, template ref or a component.
   * @param config Additional settings.
   * @returns {Promise<DialogRef>}
   */
  // open(content: ContainerContent, config?: OverlayConfig): Promise<DialogRef<any>>;
  public showSignatureValidationDetails(
    signItemParameter: SignatureDTO,
    fileParameter: FileDetailsDTO,
    onAcceptInvalidSignature?: (f: FileDetailsDTO) => void,
    onRejectInvalidSignature?: (f: FileDetailsDTO) => void
  ): void {
    this.documentService.validateSignDocument(signItemParameter, fileParameter, onAcceptInvalidSignature, onRejectInvalidSignature);
  }

  private internalSelectCertAndDecipher(
    digestAlgorithm: string,
    thumbprint: string,
    base64Fragments: Array<string>,
    successCallback: Function,
    errorCallback: Function
  ): string {
    if (!this.maxiAppletCargado) {
      console.log('cargando MaxiApplet...');
      MaxiApplet.cargarMaxiApplet(this._configuration.URL_BASE_AFIRMA);
      // advertir al usuario en caso de desfase horario
      MaxiApplet.checkTime(
        MaxiApplet.CHECKTIME_RECOMMENDED,
        MAX_MILIS,
        this._configuration.URL_BASE_AFIRMA + '/v1/checkTime'
      );
      console.log('MaxiApplet cargado');
      this.maxiAppletCargado = true;
    }

    const stringParams =
      'filters.1=thumbprint:' + digestAlgorithm + ':' + thumbprint;

    return MaxiApplet.selectCertAndDecipher(
      base64Fragments,
      stringParams,
      successCallback,
      errorCallback
    );
  }

  /**
   * Para impedir listener para cerrar aplicación cuando se llama a URL para descarga, invocacion por protocolo, etc
   * @deprecated extraer a clase nueva
   */
  public isOpeningAfirma(): boolean {
    return this.afirmaSignatureProvider.isOpeningAfirma() || MaxiApplet.isOpeningAfirma();
  }

  /**
   * Para impedir listener para cerrar aplicación cuando se llama a URL para descarga, invocacion por protocolo, etc
   * @deprecated extraer a clase nueva
   */
  public setOpeningAfirma(opening: boolean): void {
    this.afirmaSignatureProvider.setOpeningAfirma(opening);
    MaxiApplet.setOpeningAfirma(opening);
  }

}
